import React, {
  FC, useCallback, useMemo, useState,
} from 'react';
import classNames from 'classnames';
import key from 'weak-key';
import { hasExternalForm } from '@/helpers/externalForm';
import { ModalName } from '@/common/contexts/globalModalContext';
import ParallaxPageLayout, { ParallaxPageLayoutProps } from '@/common/layouts/ParallaxPageLayout/ParallaxPageLayout';
import Location, { LocationProps } from '@/pages/ContactUsPage/components/Location/Location';
import ContactUsModal, { ContactUsModalProps } from '@/pages/ContactUsPage/components/ContactUsModal/ContactUsModal';
import ExternalModalForms from '@/common/components/Modal/ExternalModalForms/ExternalModalForms';
import grid from '@/common/css/grid.module.scss';
import styles from './ContactUsPage.module.scss';

export interface ContactUsPageProps extends ParallaxPageLayoutProps {
  description?: string;
  locations?: LocationProps[];
  contactUsPopup?: ContactUsModalProps;
}

const ContactUsPage: FC<ContactUsPageProps> = ({
  description,
  locations,
  contactUsPopup,
  pardot,
  ...pageLayoutProps
}) => {
  const [officeToEmail, setOfficeToEmail] = useState<string>();

  const officesSelect = useMemo(() => (
    contactUsPopup?.offices && { ...contactUsPopup.offices, defaultValue: officeToEmail }
  ), [contactUsPopup, officeToEmail]);

  const pardotWithSelectedOffice = useMemo(() => {
    if (!officeToEmail || !pardot?.forms) {
      return pardot;
    }

    const forms = pardot.forms.map((form) => {
      if ((form.name === ModalName.contactUs) && form.url) {
        const url = new URL(form.url);
        const query = new URLSearchParams(url.search);
        query.set('officeName', officeToEmail);

        return { ...form, url: `${url.origin}${url.pathname}?${query.toString()}` };
      }

      return form;
    });

    return { ...pardot, forms };
  }, [pardot, officeToEmail]);

  const modal = useMemo(() => {
    if (!contactUsPopup || !officesSelect || hasExternalForm({ pardot, modalName: ModalName.contactUs })) {
      return null;
    }

    return (
      <ContactUsModal {...{ ...contactUsPopup, offices: officesSelect }} />
    );
  }, [
    contactUsPopup, pardot, officesSelect,
  ]);

  const handleEmailClick = useCallback((officeName: string): void => {
    setOfficeToEmail(officeName);
  }, []);

  return (
    <ParallaxPageLayout {...pageLayoutProps}>
      <div className={classNames(grid.container, styles.container)}>
        <div className={grid.row}>
          <div className={classNames(
            styles.description,
            grid.colSm10,
            grid.colMd8,
            grid.offsetSm1,
            grid.offsetMd2,
          )}
          >
            {description}
          </div>
        </div>
        <div className={grid.row}>
          <div className={classNames(grid.colMd11, grid.colLg10, grid.offsetMd1)}>
            {locations?.map((location, index) => (
              <Location key={key(location)} {...location} isOpenByDefault={!index} onEmailClick={handleEmailClick} />
            ))}
          </div>
        </div>
      </div>

      {!!pardot && (
        <ExternalModalForms
          pardot={pardotWithSelectedOffice}
          titles={{
            [ModalName.contactUs]: contactUsPopup?.modalWindowTitle,
          }}
        />
      )}
      {modal}
    </ParallaxPageLayout>
  );
};

export default ContactUsPage;
